import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type AccentColor = 'system' | 'blue' | 'green' | 'orange' | 'pink';

/**
 * Préférences d'interface (Paramètres › Préférences).
 *
 * Elles sont propres à l'APPAREIL, pas au compte, et c'est voulu : « Automatique » suit le
 * réglage clair/sombre du système de cette machine, et les notifications dépendent d'une
 * autorisation accordée navigateur par navigateur. Un même utilisateur peut légitimement
 * vouloir le thème sombre sur son ordinateur du soir et le clair au bureau.
 */
export interface Preferences {
  theme: ThemeMode;
  accent: AccentColor;

  /** Notifications natives du système d'exploitation, quand l'onglet n'est pas au premier plan. */
  osNotifications: boolean;

  /** Bandeaux affichés dans l'application quand une notification arrive. */
  browserNotifications: boolean;
}

export const DEFAULT_PREFERENCES: Preferences = {
  theme: 'light',
  accent: 'system',
  osNotifications: false,
  browserNotifications: true,
};

const STORAGE_KEY = 'pixlide.preferences';

// Le stockage peut être indisponible (navigation privée, données de site bloquées) : on
// retombe alors sur les valeurs par défaut, l'interface doit rester utilisable sans lui.
function load(): Preferences {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    return stored === null ? DEFAULT_PREFERENCES : { ...DEFAULT_PREFERENCES, ...(JSON.parse(stored) as Partial<Preferences>) };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function save(preferences: Preferences): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Préférence appliquée pour la session en cours seulement.
  }
}

interface PreferencesState extends Preferences {
  setPreferences: (preferences: Preferences) => void;
}

export const usePreferencesStore = create<PreferencesState>((set) => ({
  ...load(),

  setPreferences: (preferences) => {
    save(preferences);
    set(preferences);
  },
}));

const darkQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function applyAppearance({ theme, accent }: Preferences): void {
  const root = document.documentElement;
  const isDark = theme === 'dark' || (theme === 'auto' && darkQuery?.matches === true);

  if (isDark) {
    root.dataset.theme = 'dark';
  } else {
    delete root.dataset.theme;
  }

  if (accent === 'system') {
    delete root.dataset.accent;
  } else {
    root.dataset.accent = accent;
  }
}

/**
 * Applique l'apparence au démarrage — AVANT le premier rendu, sans quoi l'écran clignote
 * en clair — puis la tient à jour : changement de préférence, ou bascule clair/sombre du
 * système quand « Automatique » est choisi.
 */
export function initAppearance(): void {
  applyAppearance(usePreferencesStore.getState());
  usePreferencesStore.subscribe(applyAppearance);
  darkQuery?.addEventListener('change', () => applyAppearance(usePreferencesStore.getState()));
}
